import { FC } from "react"
import { observer } from "mobx-react"
import { Moment } from "moment"
import { AutoComplete, Button, Col, Form, Input, Row, Switch } from "antd"
import generatePicker from "antd/es/date-picker/generatePicker"
import { PlusOutlined } from "@ant-design/icons"

import { useStore } from "stores/utils/use-store"
import { InputColor, SubmitCancelButtons } from "components"
import GeoPickerElement from "components/geoPicker"
import { CommunityEventConfigType } from "domains/managingJson/store/types"
import {
  COMMUNITY_EVENT_AVATAR_MASKS,
  COMMUNITY_EVENT_ID_PATTERN,
  COMMUNITY_EVENT_MAX_GOALS,
  COMMUNITY_EVENT_MAX_INFO_LENGTH,
  COMMUNITY_EVENT_NAV_ICONS,
  COMMUNITY_EVENT_THEMES,
  COMMUNITY_EVENT_TOPBAR_DECORS,
  SHELL_COLOR_FIELDS,
  toOptions,
  UTC_DATE_TIME_FORMAT,
} from "./constants"
import { GoalFields } from "./GoalFields"
import { emptyGoal } from "./defaults"
import { communityEventToForm, formToCommunityEvent } from "./formMapping"
import { CommunityEventFormValues } from "./types"
import { utcMomentGenerateConfig } from "./utcPickerConfig"
import {
  countInfoCharacters,
  countriesValidator,
  endAtValidator,
  goalsValidator,
  hexColorValidator,
  infoValidator,
  visibleUntilValidator,
} from "./validators"
import s from "./styles.module.css"

const UtcDatePicker = generatePicker<Moment>(utcMomentGenerateConfig)

const UtcDateTimePicker: FC<{ value?: Moment | null; onChange?: (value: Moment | null) => void }> = ({ value, onChange }) => (
  <UtcDatePicker value={value} onChange={onChange} showTime format={UTC_DATE_TIME_FORMAT} className={s.fullWidth} />
)

const ShellColorInput: FC<{ title: string; value?: string; onChange?: (value: string) => void }> = ({ title, value, onChange }) => (
  <InputColor title={title} color={value ?? ""} onChange={color => onChange?.(color)} emptyColor="" placeholder="App default" />
)

export const EditCommunityEvent: FC<{
  editType: "create" | "edit";
  onClose: () => void;
  configType: "communityEvents";
}> = observer(({ editType, configType, onClose }) => {
  const {
    dataStore: {
      managingJsonStore,
      managingJsonStore: { selectedElement },
    },
  } = useStore()

  const existing = selectedElement.data as CommunityEventConfigType | undefined
  const [form] = Form.useForm<CommunityEventFormValues>()

  const onFinishHandler = async(values: CommunityEventFormValues) => {
    const item = { name: values.name, type: configType, data: formToCommunityEvent(values, existing) }
    managingJsonStore.funcAfterSuccess = async() => {
      onClose()
      await managingJsonStore.getData()
    }

    editType === "edit"
      ? await managingJsonStore.update(item)
      : await managingJsonStore.create(item)
  }

  return (
    <Form
      form={form}
      layout="vertical"
      initialValues={communityEventToForm(selectedElement.name, existing)}
      onFinish={onFinishHandler}
    >
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item
            label="Event id (name)"
            name="name"
            rules={[
              { required: true, message: "Please input the event id" },
              { pattern: COMMUNITY_EVENT_ID_PATTERN, message: "Lowercase letters, digits and hyphens only, up to 64" },
            ]}
          >
            <Input placeholder="halloween-hunt-2026" disabled={editType === "edit"} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Enabled" name="enabled" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item
            label="Claims enabled"
            name="claimsEnabled"
            valuePropName="checked"
            extra="Off: players see the bonuses but cannot claim them"
          >
            <Switch />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Title"
            name="title"
            rules={[{ required: true, whitespace: true, message: "Please input the title" }]}
            extra="Top bar and bonus names"
          >
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Page title" name={["shell", "pageTitle"]} extra="Event page heading. Empty shows the Title">
            <Input />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item label="Theme" name="theme" extra="Any other value shows the neutral default theme">
            <AutoComplete allowClear options={toOptions(COMMUNITY_EVENT_THEMES)} placeholder="default" />
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={8}>
          <Form.Item
            label="Start (UTC)"
            name="startAt"
            rules={[{ required: true, message: "Select the start" }]}
          >
            <UtcDateTimePicker />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="End (UTC)"
            name="endAt"
            dependencies={["startAt"]}
            rules={[{ required: true, message: "Select the end" }, endAtValidator]}
          >
            <UtcDateTimePicker />
          </Form.Item>
        </Col>
        <Col span={8}>
          <Form.Item
            label="Visible until (UTC)"
            name="visibleUntil"
            dependencies={["endAt"]}
            rules={[visibleUntilValidator]}
            extra="Empty means the end"
          >
            <UtcDateTimePicker />
          </Form.Item>
        </Col>
      </Row>

      <GeoPickerElement
        label="Countries"
        name="countries"
        withGlobal
        rules={[{ required: true, message: "Select at least one country" }, countriesValidator]}
      />

      <Form.Item
        label="Info text"
        name="info"
        extra="The whole About this event tooltip, one paragraph per line. Empty shows the default description and dates."
        rules={[infoValidator]}
      >
        <Input.TextArea
          autoSize={{ minRows: 3, maxRows: 10 }}
          showCount={{ formatter: ({ value }) => `${countInfoCharacters(value)} / ${COMMUNITY_EVENT_MAX_INFO_LENGTH}` }}
        />
      </Form.Item>

      <div className={s.sectionTitle}>Goals</div>
      <Form.List name="goals" rules={[goalsValidator]}>
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.map(({ key, name }, index) => (
              <GoalFields key={key} name={name} index={index} goalFields={fields} onRemove={() => remove(name)} />
            ))}
            <Button
              type="dashed"
              disabled={fields.length >= COMMUNITY_EVENT_MAX_GOALS}
              onClick={() => add(emptyGoal())}
              icon={<PlusOutlined />}
            >
              Add goal
            </Button>
            <Form.ErrorList errors={errors} />
          </>
        )}
      </Form.List>

      <div className={s.sectionTitle}>App shell</div>
      <Row gutter={16}>
        <Col span={6}>
          <Form.Item label="Nav label" name={["shell", "navLabel"]}>
            <Input />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Nav icon" name={["shell", "navIcon"]}>
            <AutoComplete allowClear options={toOptions(COMMUNITY_EVENT_NAV_ICONS)} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Top bar decor" name={["shell", "topbarDecor"]}>
            <AutoComplete allowClear options={toOptions(COMMUNITY_EVENT_TOPBAR_DECORS)} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item label="Avatar mask" name={["shell", "avatarMask"]}>
            <AutoComplete allowClear options={toOptions(COMMUNITY_EVENT_AVATAR_MASKS)} />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        {SHELL_COLOR_FIELDS.map(({ name, label }) => (
          <Col key={name} span={6}>
            <Form.Item name={["shell", name]} rules={[hexColorValidator]}>
              <ShellColorInput title={label} />
            </Form.Item>
          </Col>
        ))}
      </Row>

      <SubmitCancelButtons onClose={onClose} isLoading={managingJsonStore.isLoading} />
    </Form>
  )
})
